import { ReactElement } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DetailedRound } from "../../../models/round";
import { TheGameManifest } from "./thegamemanifest";
import { cardSx, formatKickoff, getPot, LINE, LIVE, MarketState, PulseDot, supportiveSx } from "./thegamecommon";

function Crest({ name }: { name: string }): ReactElement {
  return (
    <Box
      component="span"
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: "var(--sign-background-not-selected)",
        color: "var(--sign-color)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {name.slice(0, 3).toUpperCase()}
    </Box>
  );
}

interface TheGameScoreCardProps {
  round: DetailedRound;
  manifest: TheGameManifest;
  states: MarketState[];
}

// Live score, minute and match stats are not in the v1 data, so only teams, status and progress are shown.
export default function TheGameScoreCard({ round, manifest, states }: TheGameScoreCardProps): ReactElement {
  const { t } = useTranslation();
  const pot = getPot(round, t, manifest.markets.length);
  const numDecided = states.filter((s) => s.decided).length;
  const isFinished = round.status === "FINISHED" || (states.length > 0 && numDecided === states.length);
  const kickoff = new Date(manifest.match.kickoff ?? round.openTo);
  const isLive = !isFinished && kickoff < new Date();

  let status: ReactElement;
  if (isLive) {
    status = (
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 14, fontWeight: 700, color: LIVE, textTransform: "uppercase" }}>
        <PulseDot color={LIVE} />
        {t("live")}
      </Box>
    );
  } else {
    const text = isFinished
      ? t("finished")
      : formatKickoff(kickoff, t);
    status = <Typography sx={{ ...supportiveSx, fontSize: 14, fontWeight: 700 } as SxProps<Theme>}>{text}</Typography>;
  }

  return (
    <Box component="section" sx={{ ...cardSx, padding: "16px", display: "flex", flexDirection: "column", gap: "14px" } as SxProps<Theme>}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {status}
        <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>{t("the_game")}</Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", alignItems: "center", gap: "8px" }}>
        {[manifest.match.home, null, manifest.match.away].map((name, i) =>
          name === null ? (
            <Typography key={i} sx={{ fontSize: 28, fontWeight: 700, color: "var(--font-color-supportive)" }}>
              –
            </Typography>
          ) : (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", minWidth: 0 }}>
              <Crest name={name} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, textAlign: "center", overflowWrap: "anywhere" }}>{name}</Typography>
            </Box>
          )
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px", fontSize: 14, color: "var(--font-color-supportive)", borderTop: `1px solid ${LINE}`, paddingTop: "12px" }}>
        <span>{t("markets_settled", { done: numDecided, total: states.length })}</span>
        <span>
          {pot.label} <Box component="strong" sx={{ color: "var(--text-black)" }}>{pot.text}</Box>
        </span>
      </Box>
    </Box>
  );
}
